import type { PublicSentimentEntry, PublicSentimentSection } from '../../types/siteEvidence';
import { documentedSentimentEntryIsSourceBound } from './documentedLocalSentiment';
import { assessPublicSentiment, scopeSentimentEntry } from './publicSentimentScope';

/** One qualified local observation and its position in `section.entries`. */
export interface QualifiedLocalSentimentRow {
  index: number;
  entry: PublicSentimentEntry;
}

/** Recheck saved observations against their recorded date, locality and selected
 * technologies. Counts, article tone and demographic context cannot qualify a stance.
 * Returns each qualified row with its index in `section.entries`, so report
 * projections can mark exactly the rows the decision engine counts. */
export function qualifiedLocalSentimentRows(section: PublicSentimentSection | null | undefined): QualifiedLocalSentimentRow[] {
  const context = section?.queryContext;
  if (!section || !context || !/^[a-f0-9]{64}$/.test(context.fingerprint)
    || !Number.isFinite(Date.parse(section.fetchedAt))
    || section.assessment?.method !== 'dated-local-explicit-stance-v1'
    || section.assessment.status !== 'assessed') return [];
  const rows = (section.entries ?? []).map((row, index) => ({ row, index })).filter(({ row }) => row.assessmentStatus === 'assessed'
    && row.stanceKind === 'public-opinion' && typeof row.supportingSnippet === 'string'
    && typeof row.url === 'string' && (row.sourceId === 'uploaded-document'
      ? documentedSentimentEntryIsSourceBound(row,context) : /^https?:\/\//.test(row.url))
    && (row.dateBasis === 'published' || row.dateBasis === 'event')
    && Number.isFinite(Date.parse(row.observedDate ?? ''))
    && Number.isFinite(row.confidence) && row.confidence > 0 && row.confidence <= 1
    && Number.isFinite(row.sentimentScore) && Math.abs(row.sentimentScore) <= 1)
    .map(({ row, index }) => ({ index, entry: scopeSentimentEntry(row, context, section.fetchedAt) }))
    .filter(({ entry }) => entry.scope === 'site' && entry.assessmentStatus === 'assessed' && entry.confidence > 0);
  const assessment = assessPublicSentiment(rows.map(row => row.entry));
  // Conflicting or partially lost source records cannot inherit a saved score.
  return assessment.localObservationCount === section.assessment.localObservationCount
    && assessment.supportIndex === section.assessment.supportIndex ? rows : [];
}

/** Recheck saved observations against their recorded date, locality and selected
 * technologies. Counts, article tone and demographic context cannot qualify a stance. */
export function qualifiedLocalSentimentEntries(section: PublicSentimentSection | null | undefined): PublicSentimentEntry[] {
  return qualifiedLocalSentimentRows(section).map(row => row.entry);
}
